import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useRef, useState, useEffect } from 'react';
import { Box, PrefixContainer, ContentContainerLoading, IconContainer, ContentContainer } from './cell.styles.js';
import { SubtitleLoader } from './components/loaders/subtitle.js';
import { ValueLoader } from './components/loaders/value.js';
import { PrefixLoader } from './components/loaders/prefix.js';
import { CellSecondaryText } from './components/cell-secondary-text/cell-secondary-text.js';
import { CellLabel } from './components/cell-label/cell-label.js';
import { Chevron } from './components/chevron/chevron.js';
import { Postfix } from './components/postfix/postfix.js';

var _excluded = ["children", "prefix", "postfix", "subtitle", "headline", "size", "design", "disabled", "accent", "withChevron", "boxType", "dir", "type", "loading", "suffix"];

var calculateAppendixState = function calculateAppendixState(_ref) {
  var prefix = _ref.prefix,
      postfix = _ref.postfix;

  if (prefix && !postfix) {
    return 'prefixed';
  }

  if (!prefix && postfix) {
    return 'postfixed';
  }

  if (prefix && postfix) {
    return 'both';
  }

  return 'default';
};

var calculateCellState = function calculateCellState(props) {
  var size = props.size,
      design = props.design,
      accent = props.accent,
      subtitle = props.subtitle,
      headline = props.headline,
      dir = props.dir,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      _props$focused = props.focused,
      focused = _props$focused === void 0 ? false : _props$focused,
      _props$loading = props.loading,
      loading = _props$loading === void 0 ? false : _props$loading;
  return {
    appendixState: calculateAppendixState(props),
    secondaryState: subtitle || headline ? 'with-secondary' : 'none',
    size: size,
    design: design,
    accent: accent,
    dir: dir,
    disabledView: disabled,
    loadingView: loading,
    focused: focused
  };
};

var Cell = /*#__PURE__*/forwardRef(function (props, ref) {
  var _PrefixLoader;

  var children = props.children,
      prefix = props.prefix,
      postfix = props.postfix,
      subtitle = props.subtitle,
      headline = props.headline,
      _props$size = props.size,
      size = _props$size === void 0 ? 'm' : _props$size,
      _props$design = props.design,
      design = _props$design === void 0 ? 'default' : _props$design,
      _props$disabled2 = props.disabled,
      disabled = _props$disabled2 === void 0 ? false : _props$disabled2,
      _props$accent = props.accent,
      accent = _props$accent === void 0 ? 'primary' : _props$accent,
      _props$withChevron = props.withChevron,
      withChevron = _props$withChevron === void 0 ? false : _props$withChevron,
      _props$boxType = props.boxType,
      boxType = _props$boxType === void 0 ? 'button' : _props$boxType,
      _props$dir = props.dir,
      dir = _props$dir === void 0 ? 'ltr' : _props$dir,
      _props$type = props.type,
      type = _props$type === void 0 ? 'button' : _props$type,
      _props$loading2 = props.loading,
      loading = _props$loading2 === void 0 ? false : _props$loading2,
      suffix = props.suffix,
      attrs = _objectWithoutPropertiesLoose(props, _excluded);

  var boxRef = useRef(null);

  var _useState = useState(calculateCellState({
    size: size,
    design: design,
    accent: accent,
    prefix: prefix,
    postfix: postfix,
    headline: headline,
    subtitle: subtitle,
    dir: dir,
    disabled: disabled,
    loading: loading
  })),
      uiState = _useState[0],
      setUiState = _useState[1];

  useEffect(function () {
    if (!ref) {
      return;
    }

    if (typeof ref === 'function') {
      ref(boxRef.current);
    } else {
      // @ts-ignore:next-line
      ref.current = boxRef.current;
    }
  }, [ref]);
  useEffect(function () {
    setUiState(calculateCellState({
      size: size,
      design: design,
      accent: accent,
      prefix: prefix,
      postfix: postfix,
      headline: headline,
      subtitle: subtitle,
      dir: dir,
      disabled: disabled,
      loading: loading
    }));
  }, [size, design, accent, prefix, postfix, headline, subtitle, dir, disabled, loading]);

  if (loading) {
    return /*#__PURE__*/React__default.createElement(Box, _extends({
      as: boxType
    }, boxType === 'button' ? {
      type: type
    } : {}, uiState, attrs, {
      ref: boxRef,
      "aria-busy": true,
      "aria-live": "polite",
      role: "alert"
    }), prefix ? /*#__PURE__*/React__default.createElement(PrefixContainer, uiState, _PrefixLoader || (_PrefixLoader = /*#__PURE__*/React__default.createElement(PrefixLoader, {
      size: size
    }))) : null, /*#__PURE__*/React__default.createElement(ContentContainerLoading, uiState, headline ? /*#__PURE__*/React__default.createElement(SubtitleLoader, {
      size: size
    }) : null, /*#__PURE__*/React__default.createElement(ValueLoader, {
      secondaryState: uiState.secondaryState,
      size: size
    }), subtitle && !headline ? /*#__PURE__*/React__default.createElement(SubtitleLoader, {
      size: size
    }) : null));
  }

  return /*#__PURE__*/React__default.createElement(Box, _extends({
    as: boxType
  }, boxType === 'button' ? {
    type: type
  } : {}, uiState, attrs, {
    ref: boxRef,
    "aria-disabled": disabled
  }), prefix ? /*#__PURE__*/React__default.createElement(PrefixContainer, uiState, /*#__PURE__*/React__default.createElement(IconContainer, uiState, prefix), ' ') : null, /*#__PURE__*/React__default.createElement(ContentContainer, uiState, headline ? /*#__PURE__*/React__default.createElement(CellSecondaryText, {
    size: size,
    accent: accent,
    design: design
  }, headline) : null, /*#__PURE__*/React__default.createElement(CellLabel, {
    size: size,
    accent: accent,
    design: design
  }, children), subtitle && !headline ? /*#__PURE__*/React__default.createElement(CellSecondaryText, {
    size: size,
    accent: accent,
    design: design
  }, subtitle) : null), withChevron ? /*#__PURE__*/React__default.createElement(Chevron, {
    dir: dir,
    design: design,
    disabled: disabled,
    suffix: suffix
  }) : /*#__PURE__*/React__default.createElement(Postfix, {
    size: size,
    postfix: postfix,
    suffix: suffix
  }));
});

export { Cell };
